import React from "react";
import PropTypes from "prop-types";
import { formatRelativeVerbose } from "views_shared/app/javascript/utils/dateFormatters";
import MessageButton from "../../MessageButton";
import { initials } from "../../source_directory/utils";
import HighlightAttribute, { highlightValue } from "../../source_directory/HighlightAttribute";
import ExpertByline from "./ExpertByline";
import isOwnExpert from "../../experts/card/viewerGating";
import storyIdeaListActionColumnWidth from "./listActionColumn";
import BodyTextWithExpand from "../../feeds/BodyTextWithExpand";
import SavedGroupsBookmark from "../../source_directory/SavedGroupsBookmark";
import useCallForSources from "./useCallForSources";

const SQUARE_BTN_STYLE = {
  width: 36,
  height: 36,
  borderRadius: 7.5,
  border: "1px solid #dde1e5",
  background: "#fff",
  color: "#394045",
  boxShadow: "0px 0.75px 1.5px rgba(10,13,18,0.05)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  flexShrink: 0,
};

const ellipsis = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

// The Story column flexes; wrapping (not nowrap) lets the headline clamp to one line instead of
// sizing to max-content and pushing Posted + the action icons off-screen. The dek below wraps freely
// and expands in place via BodyTextWithExpand.
const clampOneLine = {
  display: "-webkit-box",
  WebkitLineClamp: 1,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  overflowWrap: "break-word",
};

function StoryIdeaCardList({ hit }) {
  const expert = hit.expert || {};
  const callForSources = useCallForSources(hit);
  const isSignedIn = !!globalThis.Qwoted?.CURRENT_USER_ID;
  const canMessage = isSignedIn && !isOwnExpert(expert);
  const postedAt = hit.posted_at_int ? formatRelativeVerbose(new Date(hit.posted_at_int * 1000)) : null;
  const byline = [expert.job_title, expert.org_name].filter(Boolean).join(" at ");

  return (
    <div
      className="sd-list-row animated faster fadeIn"
      style={{
        display: "flex",
        // Top-align columns so an expanded summary grows the row downward instead of floating the
        // avatar / Posted / action icons to the middle of a tall row.
        alignItems: "flex-start",
        gap: 14,
        padding: "15px 19px",
        borderRadius: 14,
        border: "1px solid #d2d3d4",
        boxShadow: "0px 1px 1px rgba(15,20,36,0.04)",
        backgroundColor: "#fff",
      }}
    >
      {/* Source */}
      <div
        className="sd-list-row__col"
        style={{ display: "flex", alignItems: "center", gap: 10, flex: "0 0 230px", minWidth: 0 }}
      >
        {expert.avatar_url ? (
          <img
            src={expert.avatar_url}
            alt={expert.name}
            style={{ width: 40, height: 40, borderRadius: 20, objectFit: "cover", flexShrink: 0 }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 20,
              background: "#f5f9ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              fontWeight: 700,
              fontSize: 13,
              color: "#533dd1",
            }}
          >
            {initials(expert.name)}
          </div>
        )}
        <div style={{ minWidth: 0, paddingTop: 2 }}>
          <div
            style={{
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "18.2px",
              color: "#212528",
              letterSpacing: "-0.07px",
              ...ellipsis,
            }}
          >
            <HighlightAttribute hit={hit} attribute={["expert", "name"]} fallback={expert.name} />
          </div>
          {byline && (
            <div style={{ fontSize: 12, lineHeight: "17.4px", color: "#4d5357", marginTop: 4, ...ellipsis }}>
              <ExpertByline expert={expert} orgLinkStyle={{ color: "#4d5357", textDecoration: "underline" }} />
            </div>
          )}
        </div>
      </div>

      {/* Story */}
      <div
        className="sd-list-row__col"
        style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 0 }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            lineHeight: "18.9px",
            color: "#212528",
            letterSpacing: "-0.07px",
            ...clampOneLine,
          }}
        >
          <HighlightAttribute hit={hit} attribute="title" fallback={hit.title} />
        </div>
        {hit.text && (
          <BodyTextWithExpand
            body={hit.text}
            highlightValue={highlightValue(hit, "text")}
            textClassName=""
            textStyle={{ fontSize: 12, lineHeight: "18.75px", color: "#4d5357" }}
            toggleClassName="paragraph-xs"
          />
        )}
      </div>

      {/* Posted */}
      {postedAt && (
        <div
          className="sd-list-row__col"
          style={{ flex: "0 0 90px", fontSize: 12, lineHeight: "12px", color: "#212528" }}
        >
          {postedAt}
        </div>
      )}

      {/* Actions */}
      {isSignedIn && (
        <div
          className="sd-list-row__col"
          style={{
            flex: `0 0 ${storyIdeaListActionColumnWidth(isSignedIn)}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 6,
          }}
        >
          {canMessage && (
            <>
              <MessageButton
                promoEntityId={expert.promo_entity_id}
                promoEntityType={expert.promo_entity_type}
                buttonLabel=""
                buttonClassName="btn"
                iconClassName="fa-regular fa-message"
                buttonStyle={SQUARE_BTN_STYLE}
                extraButtonClasses="mb-0"
                openInboxThread
              />
              <a
                href={callForSources.href}
                onClick={callForSources.onClick}
                aria-busy={callForSources.isPreparing}
                className="btn mb-0"
                style={SQUARE_BTN_STYLE}
                aria-label="Call for Experts/Products"
              >
                <i className={callForSources.isPreparing ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-megaphone"} />
              </a>
            </>
          )}
          <SavedGroupsBookmark
            sourceableType="Enrich::PublicPitch"
            sourceableId={hit.id}
            sourceMeta={{ name: hit.title }}
          />
        </div>
      )}
    </div>
  );
}

StoryIdeaCardList.propTypes = {
  hit: PropTypes.shape({
    title: PropTypes.string,
    text: PropTypes.string,
    posted_at_int: PropTypes.number,
    expert: PropTypes.shape({
      promo_entity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      promo_entity_type: PropTypes.string,
      name: PropTypes.string,
      slug: PropTypes.string,
      avatar_url: PropTypes.string,
      job_title: PropTypes.string,
      org_name: PropTypes.string,
      org_slug: PropTypes.string,
    }),
  }).isRequired,
};

export default StoryIdeaCardList;
